import { useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Shield, Gift, ArrowRight, Users } from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";

export default function ReferralLanding() {
  const { code } = useParams<{ code: string }>();
  const navigate = useNavigate();
  const { user } = useAuth();

  useEffect(() => {
    if (code) {
      // Store the referral code for later use during signup
      localStorage.setItem("pendingReferralCode", code);
    }

    // If user is already authenticated, redirect to profile
    if (user) {
      navigate("/profil");
      return;
    }
  }, [code, user, navigate]);

  const handleGetStarted = () => {
    navigate(`/auth?ref=${code}`);
  };

  if (!code) {
    navigate("/");
    return null;
  }

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-4">
      <div className="w-full max-w-2xl">
        {/* Header */}
        <div className="text-center mb-8">
          <div className="inline-flex items-center gap-2 mb-6">
            <Shield className="w-12 h-12 text-primary" />
            <span className="text-3xl font-bold">NIS 2 Evaluare</span>
          </div>

          <h1 className="text-4xl font-bold mb-4">
            Ai fost invitat să folosești platforma!
          </h1>
          <p className="text-xl text-muted-foreground">
            Un coleg te-a recomandat pentru evaluarea conformității NIS 2
          </p>
        </div>

        {/* Referral Info Card */}
        <Card className="shadow-form mb-8">
          <CardHeader className="text-center">
            <div className="flex justify-center mb-4">
              <div className="p-3 bg-primary/10 rounded-full">
                <Gift className="w-8 h-8 text-primary" />
              </div>
            </div>
            <CardTitle className="text-2xl">Beneficii pentru tine</CardTitle>
            <CardDescription className="text-lg">
              Pentru că ai fost recomandat, vei primi credite bonus
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-6">
            <div className="grid gap-4 md:grid-cols-2">
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary mb-2">100%</div>
                <div className="text-sm font-medium">Platformă gratuită</div>
                <div className="text-xs text-muted-foreground">
                  Evaluare completă fără costuri
                </div>
              </div>
              <div className="text-center p-4 bg-muted/50 rounded-lg">
                <div className="text-3xl font-bold text-primary mb-2">1</div>
                <div className="text-sm font-medium">Credit bonus</div>
                <div className="text-xs text-muted-foreground">
                  Pentru că ai fost recomandat
                </div>
              </div>
            </div>

            <div className="space-y-3">
              <h3 className="font-semibold">Ce poți face cu platforma:</h3>
              <ul className="space-y-2 text-sm">
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Evaluare automată a conformității NIS 2
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Analiză detaliată a riscurilor de securitate
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Raport complet de conformitate
                </li>
                <li className="flex items-center gap-2">
                  <div className="w-1.5 h-1.5 bg-primary rounded-full"></div>
                  Recomandări personalizate pentru implementare
                </li>
              </ul>
            </div>
          </CardContent>
        </Card>

        {/* Referral Code Display */}
        <Card className="mb-8">
          <CardContent className="pt-6">
            <div className="text-center space-y-4">
              <div className="text-sm text-muted-foreground">
                Codul tău de recomandare:
              </div>
              <div className="text-2xl font-mono font-bold bg-muted/50 py-3 px-6 rounded-lg inline-block">
                {code}
              </div>
              <div className="text-sm text-muted-foreground">
                Acest cod va fi aplicat automat când îți creezi contul
              </div>
            </div>
          </CardContent>
        </Card>

        {/* CTA */}
        <div className="text-center space-y-4">
          <Button
            onClick={handleGetStarted}
            size="lg"
            className="w-full md:w-auto px-8"
          >
            Începe evaluarea NIS 2
            <ArrowRight className="ml-2 h-5 w-5" />
          </Button>

          <p className="text-sm text-muted-foreground">
            Crează cont gratuit • Evaluare completă în ~15 minute
          </p>
        </div>

        {/* Social Proof */}
        <div className="mt-12 text-center">
          <div className="flex items-center justify-center gap-2 text-sm text-muted-foreground">
            <Users className="w-4 h-4" />
            <span>Peste 1000+ companii și-au evaluat conformitatea NIS 2</span>
          </div>
        </div>
      </div>
    </div>
  );
}
